var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createRef, Fragment, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import rafSchd from 'raf-schd';
import { BackgroundProvider } from '../BackgroundContext/index.js';
import { useDocument } from '../hooks/useDocument.js';
import { nodeContainsDescendant, wasContentNodeDescendant, } from '../utils/descendant.js';
import { useFloatingFullContext } from '../utils/floating/index.js';
import { findFirstKeyboardFocusableNode } from '../utils/focus.js';
import { useEventListener } from '../utils/useEventListener.js';
import { useKeypressListener } from '../utils/useKeypressListener.js';
import { useOnClickOutside } from '../utils/useOnClickOutside.js';
import { PopoverCloseSource } from './types.js';
export const PopoverPanel = props => {
    const { document } = useDocument();
    const { children, id, className, activator, autofocusTarget = 'container', open, onClose, fluidContent = true, rounded } = props, rest = __rest(props, ["children", "id", "className", "activator", "autofocusTarget", "open", "onClose", "fluidContent", "rounded"]);
    const { x, y, strategy, floating, update } = useFloatingFullContext();
    const contentNode = createRef();
    useEffect(() => {
        const schedule = rafSchd(update);
        if (open) {
            schedule();
        }
        return () => schedule === null || schedule === void 0 ? void 0 : schedule.cancel();
    }, [update, open, children]);
    const style = {
        position: strategy,
        top: '0',
        left: '0',
        transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
    };
    const focusContent = () => {
        if (autofocusTarget === 'none' || contentNode == null) {
            return;
        }
        requestAnimationFrame(() => {
            if (contentNode.current == null) {
                return;
            }
            const focusableChild = findFirstKeyboardFocusableNode(document, contentNode.current);
            if (focusableChild && autofocusTarget === 'first-node') {
                focusableChild.focus();
            }
            else {
                contentNode.current.focus();
            }
        });
    };
    const FocusTrigger = () => {
        useEffect(() => {
            focusContent();
        }, []);
        return React.createElement("div", null);
    };
    const handleClick = (event) => {
        const target = event.target;
        if (!open) {
            return;
        }
        const isDescendant = contentNode.current != null &&
            nodeContainsDescendant(contentNode.current, target);
        const isActivatorDescendant = nodeContainsDescendant(activator, target);
        if (isDescendant || isActivatorDescendant) {
            return;
        }
        onClose(PopoverCloseSource.Click);
    };
    const handleEscape = (event) => {
        if (!open) {
            return;
        }
        const target = event.target;
        const composedPath = event.composedPath();
        const wasDescendant = wasContentNodeDescendant(composedPath, contentNode);
        const isActivatorDescendant = nodeContainsDescendant(activator, target);
        if (wasDescendant || isActivatorDescendant) {
            onClose(PopoverCloseSource.EscapeKeypress);
        }
    };
    const handleFocusFirstItem = () => {
        onClose(PopoverCloseSource.FocusOut);
    };
    const handleFocusLastItem = () => {
        onClose(PopoverCloseSource.FocusOut);
    };
    useOnClickOutside(contentNode, handleClick);
    useEventListener('click', handleClick);
    useKeypressListener('Escape', handleEscape);
    const content = (React.createElement("div", { id: id, ref: contentNode, tabIndex: autofocusTarget === 'none' ? undefined : -1, className: clsx([
            'relative',
            'flex flex-col',
            rounded && 'rounded-full',
            !fluidContent ? 'max-h-none max-w-none' : 'md:max-w-sm max-h-120',
            className,
        ]) },
        open && React.createElement(FocusTrigger, null),
        children));
    return (React.createElement("div", { ref: floating, style: style, className: clsx(x === null && 'invisible') },
        React.createElement(BackgroundProvider, { backgroundType: "surface" },
            React.createElement(Transition, { show: open, as: Fragment, enter: "transition ease-out duration-200", enterFrom: "opacity-0 translate-y-1", enterTo: "opacity-100 translate-y-0", leave: "transition ease-in duration-150", leaveFrom: "opacity-100 translate-y-0", leaveTo: "opacity-0 translate-y-1" },
                React.createElement("div", Object.assign({ className: clsx([
                        'relative overflow-hidden',
                        'max-w-[calc(100vw-2rem)]',
                        !fluidContent && 'w-[calc(100vw-2rem)] md:max-w-sm',
                        'shadow-lg',
                        'border border-line-subdued',
                        'bg-surface text-content-subdued',
                        rounded ? 'rounded-full' : 'rounded-md',
                    ]) }, rest),
                    React.createElement("div", { tabIndex: 0, onFocus: handleFocusFirstItem }),
                    content,
                    React.createElement("div", { tabIndex: 0, onFocus: handleFocusLastItem }))))));
};
//# sourceMappingURL=PopoverPanel.js.map